import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Tooltip } from '@material-ui/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { dateFormat, toDayjs } from '../screens/common';
import { useGlobalStyles } from './globalStyles';
dayjs.extend(customParseFormat);

function isMonthBeforeMin(month, minDate) {
  if (!minDate) return false;
  const min = toDayjs(minDate);
  if (month.year() > min.year()) return false;
  if (month.year() < min.year()) return true;
  return month.month() < min.month();
}

function isYearBeforeMin(year, minDate) {
  if (!minDate) return false;
  return year.year() < toDayjs(minDate).year();
}

// get date style
function getDateStyle(info, config) {
  if (!info) return {};
  for (const { key, bg, label, color } of config) {
    if (info[key]) return { bg, label: info.dynamicLabel ?? label, color };
  }
  return {};
}

// custom day component to show colored days and blocked days with tooltip
function CustomDay({ dateMap, day, outsideCurrentMonth, config, resolveDayInfo, ...pickersDayProps }) {
  const classes = useGlobalStyles();
  const dateStr = day ? dateFormat(day, 'YYYY-MM-DD') : '';
  const rawInfo = !outsideCurrentMonth && dateStr ? dateMap[dateStr] : null;
  const info = resolveDayInfo ? resolveDayInfo(rawInfo) : rawInfo;
  const { bg, label, color } = getDateStyle(info, config);
  const isBlocked = info ? (info.is_cleaning_shutdown || info.is_fully_booked || info.is_already_used) : false;

  const dayEl = (
    <PickersDay
      {...pickersDayProps}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      disabled={isBlocked || pickersDayProps.disabled}
      className={classes.datePickerColoredDay}
      style={{
        ...pickersDayProps.style,
        ...(bg && !outsideCurrentMonth ? { backgroundColor: bg, color: color || '#fff' } : {}),
      }}
    />
  );

  if (isBlocked && label) {
    return (
      <Tooltip title={label} arrow>
        <span>{dayEl}</span>
      </Tooltip>
    );
  }
  return dayEl;
}

// main component
const DatePickerField = ({
  name,
  control,
  label,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  format = "DD-MM-YYYY",
  views = ['year', 'month', 'day'],
  datesCalendar = [],
  config = [],
  onMonthChange,
  clearable = false,
  resolveDayInfo,
}) => {
  const labelWithAsterisk = required ? `${label} *` : label;

  // create a map of date to info for quick lookup in custom day rendering
  const dateMap = useMemo(() => {
    if (!datesCalendar.length) return {};
    const map = {};
    datesCalendar.forEach((d) => { map[d.date] = d; });
    return map;
  }, [datesCalendar]);

  // check if there are any dates with availability info to decide whether to use custom day rendering
  const hasAvailability = datesCalendar.length > 0 && config.length > 0;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const value = toDayjs(field.value, format);
        return (
        <DatePicker
          {...field}
          label={labelWithAsterisk}
          onChange={(date) => field.onChange(date ?? null)}
          onMonthChange={onMonthChange}
          value={value}
          format={format}
          views={views}
          disabled={disabled}
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          shouldDisableMonth={(month) => isMonthBeforeMin(month, minDate)}
          shouldDisableYear={(year) => isYearBeforeMin(year, minDate)}
          {...(hasAvailability && {
            slots: { day: CustomDay },
            slotProps: {
              textField: { onBlur: field.onBlur, fullWidth: true, error: !!error, helperText: error?.message },
              field: { clearable: clearable },
              day: { dateMap, config, resolveDayInfo },
              popper: { placement: "bottom-start", modifiers: [{ name: "flip", enabled: true, options: { fallbackPlacements: ["top-start"] } }, { name: "preventOverflow", enabled: true, options: { boundary: "viewport" } }] },
            },
          })}
          {...(!hasAvailability && {
            slotProps: {
              textField: { onBlur: field.onBlur, fullWidth: true, error: !!error, helperText: error?.message },
              field: { clearable: clearable },
              popper: { placement: "bottom-start", modifiers: [{ name: "flip", enabled: true, options: { fallbackPlacements: ["top-start"] } }, { name: "preventOverflow", enabled: true, options: { boundary: "viewport" } }] },
            },
          })}
          renderInput={(params) => (
            <TextField {...params} fullWidth error={!!error} helperText={error?.message} />
          )}
        />
        );
      }}
    />
  );
};

export default DatePickerField;
